import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const useContactState = (contactId, initialState) => {
  const [currentState, setCurrentState] = useState(initialState || null);
  const [states, setStates] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [closeOnGained, setCloseOnGained] = useState(true);
  const [closeOnLost, setCloseOnLost] = useState(true);

  useEffect(() => {
    setCurrentState(initialState || null);
  }, [initialState?.id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [statesRes, reasonsRes, closeRes] = await Promise.all([
          api.get("/contact-states"),
          api.get("/lost-reasons"),
          api.get("/companies/close-settings"),
        ]);
        if (!cancelled) {
          setStates(statesRes.data);
          setLostReasons(reasonsRes.data);
          setCloseOnGained(closeRes.data.closeOnGained !== false);
          setCloseOnLost(closeRes.data.closeOnLost !== false);
        }
      } catch (err) {
        // silently fail — states list is non-critical
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // feat_395: opts = { lostReasonText, autoClose, source }. Con autoClose el
  // BACKEND cierra el ticket según closeOnGained/closeOnLost (feat_394); el
  // frontend no debe cerrarlo además (doble cierre).
  const changeState = useCallback(async (stateId, lostReasonId, opts = {}) => {
    setLoading(true);
    try {
      const body = { stateId: stateId ?? null };
      if (lostReasonId) body.lostReasonId = lostReasonId;
      const text = typeof opts.lostReasonText === "string" ? opts.lostReasonText.trim() : "";
      if (text) body.lostReasonText = text;
      if (opts.autoClose) body.autoClose = true;
      if (opts.source) body.source = opts.source;
      const res = await api.put(`/contacts/${contactId}/state`, body);
      const newState = res.data.contactState || null;
      setCurrentState(newState);
      return newState;
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  return { currentState, states, lostReasons, loading, changeState, closeOnGained, closeOnLost };
};

export default useContactState;
